import { AccessLevel } from "@/data/AccessLevel";
import { updateCustomer } from "@/data/customer";
import { useCurrentUid } from "@/data/studio-react/firebase/useFirestoreAuth";
import { ArchiveBoxIcon, TrashIcon } from "@heroicons/react/24/solid";
import { Timestamp } from "firebase/firestore";
import { useMemo } from "react";
import { toast } from "sonner";
import { Customer } from "types";
import { AccessLevelControl } from "../AccessLevelControl";
import { Button } from "../Buttons";
import { useCartForCustomer } from "./Tabs/hooks/useCartForCustomer";
import { useCustomerBalance } from "./Tabs/hooks/useCustomerBalance";
import { useCustomerInvoices } from "./Tabs/hooks/useCustomerInvoices";
import { Card } from "./Tabs/Settings/SettingsGeneral";

export const CustomerDangerZone = ({
  customer,
  marketId,
}: {
  customer: Customer;
  marketId: string;
}) => {
  let { extras, lotsBroughtBySale, lotsSoldBySale } = useCartForCustomer(
    customer,
    marketId
  );

  let currentUid = useCurrentUid();
  let customerId = customer?.id;
  let currentBalance = useCustomerBalance(marketId, customer.id);
  let invoices = useCustomerInvoices(marketId, customer.id, {
    page: 1,
    perPage: 5,
  });

  let cartExtras = extras?.itemsById ?? {};

  let allowToDeleteReason: string | null = useMemo(() => {
    let reasons = [];
    if (invoices.data?.length > 0) {
      reasons.push("Customer has invoices");
    }

    if (currentBalance.customerBalances?.tradeReceivable) {
      reasons.push("Customer has a trade receivable");
    }

    if (currentBalance.customerBalances?.tradePayable) {
      reasons.push("Customer has a trade payable");
    }

    if (lotsBroughtBySale.length > 0) {
      reasons.push("Customer has lots brought");
    }

    if (lotsSoldBySale.length > 0) {
      reasons.push("Customer has lots sold");
    }

    if (Object.keys(cartExtras).length > 0) {
      reasons.push("Customer has cart extras");
    }

    if (reasons.length !== 0) {
      return reasons.join(", ");
    }
    return null;
  }, [lotsBroughtBySale, lotsSoldBySale, cartExtras, currentBalance]);

  async function _archiveCustomer() {
    try {
      let update: Partial<Customer> = {
        status: "archived",
        deleteAfter: null,
      };
      await updateCustomer(marketId, currentUid, customerId, update);
      toast.success("Customer archived");
    } catch (error) {
      toast.error("Failed to archive customer");
    }
  }

  async function _deleteCustomer() {
    try {
      let update: Partial<Customer> = {
        status: "deleted",
      };
      await updateCustomer(marketId, currentUid, customerId, update);
      // 1 Month from Now
      let deleteAfter = new Date();
      deleteAfter.setMonth(deleteAfter.getMonth() + 1);
      await updateCustomer(marketId, currentUid, customerId, {
        status: "deleted",
        deleteAfter: Timestamp.fromDate(deleteAfter),
      });
      toast.success("Customer deleted");
    } catch (error) {
      toast.error("Failed to delete customer");
    }
  }

  async function _unarchiveCustomer() {
    try {
      await updateCustomer(marketId, currentUid, customerId, {
        status: null,
        deleteAfter: null,
      });
      toast.success("Customer unarchived");
    } catch (error) {
      toast.error("Failed to unarchive customer");
    }
  }

  async function _undeleteCustomer() {
    try {
      await updateCustomer(marketId, currentUid, customerId, {
        status: null,
        deleteAfter: null,
      });
      toast.success("Customer undeleted");
    } catch (error) {
      toast.error("Failed to undelete customer");
    }
  }

  return (
    <div className="pt-2">
      <Card className="!grid-cols-1 w-full">
        <div className="flex flex-col w-full">
          <h3 className="pb-4 text-md font-medium leading-6 text-gray-900">
            Danger Zone
          </h3>
          <div className="flex flex-col gap-4">
            <AccessLevelControl minAccessLevel={AccessLevel.Admin}>
              {/* ARCHIVE SECTION */}
              {customer?.status === "archived" && (
                <UnarchiveCustomer onClick={_unarchiveCustomer} />
              )}
              {customer?.status !== "archived" &&
                customer?.status !== "deleted" && 
                allowToDeleteReason && (
                  <ArchiveCustomer onClick={_archiveCustomer} />
                )}
            </AccessLevelControl>
            <AccessLevelControl minAccessLevel={AccessLevel.Admin}>
              {/* DELETE SECTION */}
              {customer?.status === "deleted" && (
                <UndeleteCustomer
                  onClick={_undeleteCustomer}
                  disabled={false}
                />
              )}
              {customer?.status !== "deleted" && !allowToDeleteReason && (
                <DeleteCustomer onClick={_deleteCustomer} disabled={false} />
              )}
            </AccessLevelControl>
          </div>
        </div>
      </Card>
    </div>
  );
};

function ArchiveCustomer({ onClick }: { onClick: () => void }) {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50">
      <div className="flex-1">
        <h4 className="text-sm font-medium text-gray-900">Archive Customer</h4>
        <p className="text-sm text-gray-500 mt-1">
          Archiving removes a customer from view but still keeps a record of
          them attached to old sales and lots.
        </p>
      </div>
      <Button
        className="hover:opacity-80 ml-4"
        onClick={onClick}
        variant="primary-light"
        icon={<ArchiveBoxIcon className="h-4 w-4" />}
      >
        Archive
      </Button>
    </div>
  );
}

function DeleteCustomer({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50">
      <div className="flex-1">
        <h4 className="text-sm font-medium text-gray-900">Delete Customer</h4>
        <p className="text-sm text-gray-500 mt-1">
          Remove this person from the system.
        </p>
      </div>
      <Button
        isDisabled={disabled}
        className="hover:opacity-80 disabled:opacity-50 ml-4"
        onClick={onClick}
        variant="danger"
        icon={<TrashIcon className="h-4 w-4" />}
      >
        Delete
      </Button>
    </div>
  );
}

function UnarchiveCustomer({ onClick }: { onClick: () => void }) {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50">
      <div className="flex-1">
        <h4 className="text-sm font-medium text-gray-900">
          Restore Archived Customer
        </h4>
        <p className="text-sm text-gray-500 mt-1">
          Restoring customers will bring them back to the system.
        </p>
      </div>
      <Button
        className="hover:opacity-80 ml-4"
        onClick={onClick}
        variant="tertiary"
        icon={<ArchiveBoxIcon className="h-4 w-4" />}
      >
        Unarchive
      </Button>
    </div>
  );
}

function UndeleteCustomer({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50">
      <div className="flex-1">
        <h4 className="text-sm font-medium text-gray-900">
          Restore Deleted Customer
        </h4>
        <p className="text-sm text-gray-500 mt-1">
          Restoring customers will bring them back to the system.
        </p>
      </div>
      <Button
        isDisabled={disabled}
        className="hover:opacity-80 ml-4"
        onClick={onClick}
        variant="danger"
        icon={<TrashIcon className="h-4 w-4" />}
      >
        Restore
      </Button>
    </div>
  );
}
